import { useCallback, useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Inbox, PhoneCall, CheckCheck, LogOut, ExternalLink, Trash2, X, Loader2, HardHat, HandHeart, Map, Route, Coins } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, assetUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { AdminProjects } from "@/components/AdminProjects";
import { AdminPartnerApplications } from "@/components/AdminPartnerApplications";
import { AdminTerritories } from "@/components/AdminTerritories";
import { AdminProspects } from "@/components/AdminProspects";
import { AdminCommissions } from "@/components/AdminCommissions";
import { AdminModalOverlay, AdminModalCard, AdminModalBody } from "@/components/AdminModal";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("demandes");

  const fetchContacts = useCallback(() => {
    api
      .get("/admin/contacts")
      .then((res) => setContacts(res.data))
      .catch(() => toast.error("Impossible de charger les demandes"))
      .finally(() => setLoading(false));
  }, []);

  const isAdmin = user && user.role === "CENTRAL";

  useEffect(() => {
    if (isAdmin) fetchContacts();
  }, [isAdmin, fetchContacts]);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-navy" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const nouveaux = contacts.filter((c) => c.status === "nouveau").length;

  const toggleStatus = async (c) => {
    const next = c.status === "nouveau" ? "rappelé" : "nouveau";
    try {
      await api.patch(`/admin/contacts/${c.id}`, { status: next });
      toast.success(next === "rappelé" ? `${c.name} marqué comme rappelé` : `${c.name} repassé en nouveau`);
      setDetail(null);
      fetchContacts();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la demande de ${c.name} ?`)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      toast.success("Demande supprimée");
      setDetail(null);
      fetchContacts();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-navy text-white md:flex">
        <div className="flex items-center gap-3 px-6 py-7">
          <Logo boxed className="h-7 w-auto" boxClassName="rounded-xl px-2.5 py-1.5" />
          <div>
            <p className="font-display text-xl font-semibold">cozyhome</p>
            <p className="text-[10px] uppercase tracking-widest text-white/50">Espace pro</p>
          </div>
        </div>
        <nav className="mt-6 flex-1 space-y-1 px-4">
          <button
            data-testid="admin-tab-demandes"
            onClick={() => setTab("demandes")}
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors duration-300 ${
              tab === "demandes" ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Inbox className="h-4 w-4 text-cognac-light" /> Demandes de rappel
            {nouveaux > 0 && (
              <span data-testid="admin-new-count" className="ml-auto rounded-full bg-cognac px-2 py-0.5 text-xs">{nouveaux}</span>
            )}
          </button>
          <button
            data-testid="admin-tab-chantiers"
            onClick={() => setTab("chantiers")}
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors duration-300 ${
              tab === "chantiers" ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
            }`}
          >
            <HardHat className="h-4 w-4 text-cognac-light" /> Chantiers clients
          </button>
          <button
            data-testid="admin-tab-candidatures"
            onClick={() => setTab("candidatures")}
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors duration-300 ${
              tab === "candidatures" ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
            }`}
          >
            <HandHeart className="h-4 w-4 text-cognac-light" /> Candidatures partenaires
          </button>
          <button
            data-testid="admin-tab-territoires"
            onClick={() => setTab("territoires")}
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors duration-300 ${
              tab === "territoires" ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Map className="h-4 w-4 text-cognac-light" /> Réseau &gt; Territoires
          </button>
          <button
            data-testid="admin-tab-prospects"
            onClick={() => setTab("prospects")}
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors duration-300 ${
              tab === "prospects" ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Route className="h-4 w-4 text-cognac-light" /> Prospects &amp; routage
          </button>
          <button
            data-testid="admin-tab-commissions"
            onClick={() => setTab("commissions")}
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors duration-300 ${
              tab === "commissions" ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Coins className="h-4 w-4 text-cognac-light" /> Commissions Réseau
          </button>
        </nav>
        <div className="space-y-1 px-4 pb-7">
          <Link to="/" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-white/60 transition-colors duration-300 hover:bg-white/10 hover:text-white">
            <ExternalLink className="h-4 w-4" /> Voir le site
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-white/60 transition-colors duration-300 hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between bg-navy px-5 py-4 text-white md:hidden">
        <div className="flex items-center gap-2.5">
          <Logo boxed className="h-6 w-auto" boxClassName="rounded-lg px-2 py-1" />
          <span className="font-display text-lg font-semibold">cozyhome</span>
        </div>
        <button
          data-testid="admin-logout-button-mobile"
          onClick={async () => { await logout(); navigate("/admin/login"); }}
          className="text-white/70"
          aria-label="Déconnexion"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      <main className="px-5 py-8 sm:px-8 md:ml-64">
        <div className="mb-6 flex gap-2 md:hidden">
          {[
            ["demandes", "Demandes", "admin-tab-demandes-mobile"],
            ["chantiers", "Chantiers", "admin-tab-chantiers-mobile"],
            ["candidatures", "Candidatures", "admin-tab-candidatures-mobile"],
            ["territoires", "Territoires", "admin-tab-territoires-mobile"],
            ["prospects", "Prospects", "admin-tab-prospects-mobile"],
          ].map(([id, label, tid]) => (
            <button
              key={id}
              data-testid={tid}
              onClick={() => setTab(id)}
              className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-300 ${
                tab === id ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "demandes" ? (
          <>
        <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Demandes de rappel</h1>
        <p className="mt-2 text-sm text-slate-500">Chaque demande du formulaire arrive ici — et dans votre boîte mail.</p>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {[
            { label: "Total", value: contacts.length, icon: Inbox, testid: "admin-stat-total" },
            { label: "À rappeler", value: nouveaux, icon: PhoneCall, testid: "admin-stat-new" },
            { label: "Rappelés", value: contacts.length - nouveaux, icon: CheckCheck, testid: "admin-stat-done" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="rounded-2xl border border-slate-200 bg-white p-5">
              <s.icon className="h-5 w-5 text-cognac" />
              <p className="mt-3 font-display text-3xl font-semibold text-navy">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>
          ) : contacts.length === 0 ? (
            <p data-testid="admin-empty-state" className="py-16 text-center text-sm text-slate-400">
              Aucune demande pour le moment.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-400">
                    <th className="px-5 py-4">Reçu le</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Projet</th>
                    <th className="px-5 py-4">Budget</th>
                    <th className="px-5 py-4">Statut</th>
                    <th className="px-5 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr
                      key={c.id}
                      data-testid={`admin-contact-row-${c.id}`}
                      onClick={() => setDetail(c)}
                      className="cursor-pointer border-b border-slate-100 transition-colors duration-200 hover:bg-cream"
                    >
                      <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmtDate(c.created_at)}</td>
                      <td className="px-5 py-4">
                        <p className="font-bold text-navy">{c.name}</p>
                        <p className="text-xs text-slate-400">{c.phone}</p>
                      </td>
                      <td className="px-5 py-4 text-slate-600">{c.project_type}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-600">{c.budget}</td>
                      <td className="px-5 py-4">
                        <span
                          data-testid={`admin-status-${c.id}`}
                          className={`rounded-full px-3 py-1 text-xs font-bold ${
                            c.status === "nouveau" ? "bg-cognac/15 text-cognac-dark" : "bg-sage/20 text-navy"
                          }`}
                        >
                          {c.status === "nouveau" ? "À rappeler" : "Rappelé"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right text-slate-300">
                        <ExternalLink className="ml-auto h-4 w-4" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        ) : tab === "chantiers" ? (
          <>
            <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Chantiers clients</h1>
            <p className="mt-2 text-sm text-slate-500">
              Jalons, photos d'avancement et lien de suivi à partager à vos clients.
            </p>
            <div className="mt-8">
              <AdminProjects />
            </div>
          </>
        ) : tab === "candidatures" ? (
          <>
            <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Candidatures partenaires</h1>
            <p className="mt-2 text-sm text-slate-500">
              Les candidatures reçues via le formulaire « Devenir Partenaire » arrivent ici.
            </p>
            <div className="mt-8">
              <AdminPartnerApplications />
            </div>
          </>
        ) : tab === "territoires" ? (
          <>
            <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Réseau &gt; Territoires</h1>
            <p className="mt-2 text-sm text-slate-500">
              Vision nationale des secteurs commerciaux CozyHome — jamais visible publiquement.
            </p>
            <div className="mt-8">
              <AdminTerritories />
            </div>
          </>
        ) : tab === "commissions" ? (
          <>
            <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Commissions Réseau</h1>
            <p className="mt-2 text-sm text-slate-500">
              Commissions entreprises, répartition Partenaire / Centrale, avantages, Voyage CozyHome et prime d'excellence.
            </p>
            <div className="mt-8">
              <AdminCommissions />
            </div>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Prospects &amp; routage</h1>
            <p className="mt-2 text-sm text-slate-500">
              File des projets reçus via « Parler de mon projet », dont les secteurs non couverts à réaffecter.
            </p>
            <div className="mt-8">
              <AdminProspects />
            </div>
          </>
        )}
      </main>

      <AnimatePresence>
        {detail && (
          <AdminModalOverlay testId="admin-detail-modal" onClose={() => setDetail(null)}>
            <AdminModalCard>
              <div className="flex shrink-0 items-start justify-between border-b border-slate-100 px-7 py-6 sm:px-9">
                <div>
                  <h2 className="font-display text-2xl font-medium text-navy">{detail.name}</h2>
                  <p className="mt-1 text-xs text-slate-400">{fmtDate(detail.created_at)}</p>
                </div>
                <button
                  data-testid="admin-detail-close"
                  onClick={() => setDetail(null)}
                  className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors duration-300 hover:bg-navy hover:text-white"
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <AdminModalBody>
              <dl className="space-y-3 text-sm">
                {[
                  ["Téléphone", detail.phone],
                  ["Email", detail.email],
                  ["Type de projet", detail.project_type],
                  detail.property_type && ["Type de bien", detail.property_type],
                  (detail.surface_range || detail.surface) && ["Surface", detail.surface_range || detail.surface],
                  detail.postal_code && ["Code postal", detail.postal_code],
                  detail.city && ["Commune", detail.city],
                  detail.avancement && ["Avancement", detail.avancement],
                  ["Budget", detail.budget],
                  ["Délai", detail.timeline],
                  detail.callback_slot && ["Créneau de rappel", detail.callback_slot],
                  detail.model && ["Modèle", detail.model],
                  detail.priorities?.length && ["Priorités", detail.priorities.join(", ")],
                  detail.accompaniment && ["Accompagnement", detail.accompaniment],
                  detail.needs?.length && ["Besoins", detail.needs.join(", ")],
                  detail.work_types?.length && ["Travaux envisagés", detail.work_types.join(", ")],
                  ["Consentement marketing", detail.marketing_optin ? "Oui" : "Non"],
                ].filter(Boolean).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                    <dt className="shrink-0 text-slate-400">{k}</dt>
                    <dd className="text-right font-semibold text-navy">{v}</dd>
                  </div>
                ))}
                {detail.message && (
                  <div className="rounded-xl bg-cream p-4 text-slate-600">{detail.message}</div>
                )}
                {detail.attachments?.length > 0 && (
                  <div data-testid="admin-detail-attachments" className="rounded-xl bg-cream p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Pièces jointes</p>
                    <ul className="mt-2 space-y-1.5">
                      {detail.attachments.map((f, i) => (
                        <li key={f.path || i}>
                          <a
                            data-testid={`admin-attachment-${i}`}
                            href={assetUrl(`/api/contact-files/${f.path}`)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 text-sm font-semibold text-cognac transition-colors hover:text-navy"
                          >
                            <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {f.name || "document"}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </dl>
              </AdminModalBody>

              <div className="flex shrink-0 flex-wrap gap-3 border-t border-slate-100 px-7 py-5 sm:px-9">
                <a
                  data-testid="admin-detail-call-button"
                  href={`tel:${detail.phone}`}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-navy px-5 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-cognac"
                >
                  <PhoneCall className="h-4 w-4" /> Appeler
                </a>
                <button
                  data-testid="admin-detail-toggle-status"
                  onClick={() => toggleStatus(detail)}
                  className="flex-1 rounded-full border border-navy/15 px-5 py-3.5 text-sm font-bold text-navy transition-colors duration-300 hover:border-cognac hover:text-cognac"
                >
                  {detail.status === "nouveau" ? "Marquer rappelé" : "Repasser en nouveau"}
                </button>
                <button
                  data-testid="admin-detail-delete"
                  onClick={() => remove(detail)}
                  className="rounded-full border border-red-200 p-3.5 text-red-400 transition-colors duration-300 hover:bg-red-500 hover:text-white"
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </AdminModalCard>
          </AdminModalOverlay>
        )}
      </AnimatePresence>
    </div>
  );
}
